import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Spinner,
  ListGroup,
  Badge,
  ProgressBar,
  Tab,
  Tabs,
  Table,
  Accordion
} from "react-bootstrap";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  setDoc,
  writeBatch,
  doc,
  getFirestore,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import axios from "axios";
import { FUNCTION_URLS } from "../../config/functionUrls";

const CurriculumExtractor = ({ ruc = "", vacanteId = null }) => {
  // Estado para manejar la carga de PDF.js
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Cambio para manejar múltiples archivos
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [textFragments, setTextFragments] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [indexProgress, setIndexProgress] = useState(0);
  const [docId, setDocId] = useState("");
  const [totalProgress, setTotalProgress] = useState(0);
  const [processingResults, setProcessingResults] = useState([]);

  // Opciones específicas para currículums
  const [maxFragmentLength, setMaxFragmentLength] = useState(500); // Mayor para currículums
  const [detectSections, setDetectSections] = useState(true);
  const [procesarDatos, setProcesarDatos] = useState(true);

  // Opciones para indexación
  const [docTitle, setDocTitle] = useState("");
  const [docDescription, setDocDescription] = useState("Currículum Vitae");

  // Opciones para el procesamiento de embeddings
  const [processingEmbeddings, setProcessingEmbeddings] = useState(false);
  const [embeddingsProgress, setEmbeddingsProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingAll, setProcessingAll] = useState(false);

  // Referencia a Firestore
  const db = getFirestore(firebaseApp);

  // Cargar PDF.js dinámicamente
  useEffect(() => {
    const loadPdfJs = async () => {
      try {
        // Cargar PDF.js de manera más robusta
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js";
        script.async = true;

        // Promisificar la carga del script
        const scriptLoaded = new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });

        document.body.appendChild(script);

        // Esperar a que el script se cargue
        await scriptLoaded;

        // Configurar worker
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

        setPdfJsLoaded(true);
      } catch (error) {
        console.error("Error al cargar PDF.js:", error);
        setError(
          "Error al cargar la biblioteca PDF.js. Intente recargar la página."
        );
      }
    };

    loadPdfJs();

    return () => {
      // Intentar limpiar los scripts si es necesario
      const scripts = document.querySelectorAll('script[src*="pdfjs-dist"]');
      scripts.forEach((s) => {
        try {
          document.body.removeChild(s);
        } catch (e) {
          // Ignorar errores al eliminar
        }
      });
    };
  }, []);

  // Efecto para generar ID de documento basado en nombre de archivo del archivo actual
  useEffect(() => {
    if (files.length > 0 && currentFileIndex < files.length) {
      const currentFile = files[currentFileIndex];
      // Generar un ID sugerido basado en el nombre del archivo (para CV)
      const timestamp = new Date().getTime();
      const uniqueId = `cv_${timestamp}_${Math.floor(Math.random() * 1000)}`;

      setDocId(uniqueId);
      setDocTitle(currentFile.name.replace(/\.pdf$/, ""));
    }
  }, [files, currentFileIndex]);

  const renderRucInfo = () => {
    return (
      <Alert variant="info" className="mb-3">
        <small>
          Procesando currículums para la empresa con RUC: <strong>{ruc}</strong>
          {vacanteId && (
            <span>
              {" "}
              - Vacante ID: <strong>{vacanteId}</strong>
            </span>
          )}
        </small>
      </Alert>
    );
  };

  // Función para mostrar notificaciones con SweetAlert2
  const showNotification = (title, message, icon = "success") => {
    Swal.fire({
      title: title,
      text: message,
      icon: icon,
      timer: 3000,
      timerProgressBar: true,
      toast: false,
      position: "center",
      showConfirmButton: false,
    });
  };

  // Manejar selección de archivos múltiples
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).filter(
        (file) => file.type === "application/pdf"
      );

      if (selectedFiles.length === 0) {
        setError("Todos los archivos deben ser PDF");
        return;
      }

      if (selectedFiles.length !== e.target.files.length) {
        setError("Se han filtrado archivos que no son PDF");
      } else {
        setError(null);
      }

      setFiles(selectedFiles);
      setCurrentFileIndex(0);
      setSuccess(null);
      setResult(null);
      setTextFragments([]);
      setProcessingResults([]);
    }
  };

  // Extracción de texto de la estructura de PDF.js - optimizado para CV
  // Reemplazar la función extractCVPageText con esta versión mejorada
  const extractCVPageText = (textContent) => {
    let lastY = -1;
    let text = "";

    // Ordenar los elementos de texto considerando mejor posicionamiento vertical
    const textItems = [...textContent.items].sort((a, b) => {
      // Usar un umbral más pequeño para detectar cambios de línea
      if (Math.abs(a.transform[5] - b.transform[5]) < 3) {
        return a.transform[4] - b.transform[4]; // Ordenar por posición horizontal
      }
      // Invertir el orden (de arriba hacia abajo) para mejor procesamiento
      return b.transform[5] - a.transform[5];
    });

    // Crear mapa de posiciones Y para agrupar texto en líneas
    const yPositions = textItems.map((item) => item.transform[5]);
    const uniqueYPositions = [...new Set(yPositions.map((y) => Math.round(y)))];
    uniqueYPositions.sort((a, b) => b - a); // Ordenar de arriba hacia abajo

    // Procesar texto por líneas en lugar de por elementos individuales
    for (const yPos of uniqueYPositions) {
      const lineItems = textItems
        .filter((item) => Math.abs(item.transform[5] - yPos) < 5)
        .sort((a, b) => a.transform[4] - b.transform[4]);

      if (lineItems.length > 0) {
        // Agregar salto de línea entre líneas diferentes
        if (text.length > 0) text += "\n";

        // Unir elementos de esta línea
        let lineText = "";
        for (const item of lineItems) {
          if (
            lineText &&
            !lineText.endsWith(" ") &&
            !item.str.startsWith(" ")
          ) {
            lineText += " ";
          }
          lineText += item.str;
        }

        text += lineText.trim();
      }
    }

    // Limpieza general del texto con mejor preservación de estructura
    return text
      .replace(/\s+\n/g, "\n") // Eliminar espacios antes de saltos de línea
      .replace(/\n\s+/g, "\n") // Eliminar espacios después de saltos de línea
      .replace(/\n{4,}/g, "\n\n\n") // Permitir hasta 3 saltos de línea consecutivos para preservar estructura
      .trim();
  };

  // Función para analizar el CV con la IA mediante Cloud Function
  const analyzeCV = async (cvText, fileName, uniqueId) => {
    try {
      setSuccess("Analizando el CV con IA...");

      // URL de la Cloud Function
      const functionUrl = FUNCTION_URLS.hiroAnalizarCvDirecto;

      // Llamar a la Cloud Function
      const response = await axios.post(functionUrl, {
        text: cvText,
        filename: fileName,
        documentId: uniqueId,
        ruc: ruc,
        vacanteId: vacanteId || null,
      });

      // Verificar respuesta
      if (!response.data || !response.data.success) {
        console.error(
          "Error en el análisis de CV:",
          response.data?.error || "Error desconocido"
        );
        return { success: false, data: null };
      }

      return {
        success: true,
        data: response.data.result,
        summary: response.data.summary || null,
      };
    } catch (error) {
      console.error("Error al analizar CV con IA:", error);
      return {
        success: false,
        data: null,
      };
    }
  };

  // Función para crear fragmentos a partir de datos estructurados por IA
  const createFragmentsFromStructuredData = (
    structuredData,
    uniqueId,
    pageCount
  ) => {
    const fragments = [];
    let fragmentIndex = 0;

    // Función auxiliar para agregar fragmentos
    const addFragment = (
      text,
      section,
      context,
      page = 1,
      isHeader = false
    ) => {
      if (!text || text.trim() === "") return;

      fragments.push({
        text: cleanText(text.trim()),
        page,
        paragraph_index: fragmentIndex++,
        section,
        section_header: isHeader ? text : null,
        context: context || section,
        is_section_header: isHeader,
        document_id: uniqueId,
        fragment_id: `${uniqueId}_f${fragmentIndex}`,
      });
    };

    // 1. Información personal
    if (structuredData.informacion_personal) {
      addFragment(
        "Información Personal",
        "datos_personales",
        "header",
        1,
        true
      );

      const infoPersonal = structuredData.informacion_personal;

      if (infoPersonal.nombre_completo) {
        addFragment(
          `Nombre: ${infoPersonal.nombre_completo}`,
          "datos_personales",
          "nombre"
        );
      }

      if (infoPersonal.contacto) {
        // Si es string simple
        if (typeof infoPersonal.contacto === "string") {
          addFragment(
            `Contacto: ${infoPersonal.contacto}`,
            "datos_personales",
            "contacto"
          );
        }
        // Si es objeto con propiedades
        else if (typeof infoPersonal.contacto === "object") {
          Object.entries(infoPersonal.contacto).forEach(([key, value]) => {
            if (value) {
              addFragment(`${key}: ${value}`, "datos_personales", key);
            }
          });
        }
      }

      if (infoPersonal.ubicacion) {
        addFragment(
          `Ubicación: ${infoPersonal.ubicacion}`,
          "datos_personales",
          "ubicacion"
        );
      }
    }

    // 2. Resumen profesional
    if (structuredData.resumen_profesional) {
      addFragment("Resumen Profesional", "extracto", "header", 1, true);
      addFragment(structuredData.resumen_profesional, "extracto", "resumen");
    }

    // 3. Experiencia laboral
    if (
      structuredData.experiencia_laboral &&
      structuredData.experiencia_laboral.length > 0
    ) {
      addFragment("Experiencia Laboral", "experiencia", "header", 1, true);

      structuredData.experiencia_laboral.forEach((experiencia, idx) => {
        // Título con empresa
        let titulo = experiencia.puesto || "";
        if (experiencia.empresa) {
          titulo += experiencia.puesto
            ? ` en ${experiencia.empresa}`
            : experiencia.empresa;
        }

        addFragment(titulo, "experiencia", "puesto", 1, true);

        // Fechas y duración
        let periodoTexto = "";
        if (experiencia.fecha_inicio || experiencia.fecha_fin) {
          periodoTexto = `${experiencia.fecha_inicio || ""} - ${experiencia.fecha_fin || "Actual"
            }`;
          addFragment(`Período: ${periodoTexto}`, "experiencia", "periodo");
        }

        if (experiencia.duracion) {
          addFragment(
            `Duración: ${experiencia.duracion}`,
            "experiencia",
            "duracion"
          );
        }

        if (experiencia.ubicacion) {
          addFragment(
            `Ubicación: ${experiencia.ubicacion}`,
            "experiencia",
            "ubicacion"
          );
        }

        // Descripción y logros
        if (experiencia.descripcion_logros) {
          // Si es un texto largo, dividirlo en fragmentos más pequeños
          if (experiencia.descripcion_logros.length > maxFragmentLength) {
            const subFragments = splitLongParagraph(
              experiencia.descripcion_logros,
              maxFragmentLength
            );
            subFragments.forEach((subFragment, subIdx) => {
              addFragment(subFragment, "experiencia", "descripcion", 1, false);
            });
          } else {
            addFragment(
              experiencia.descripcion_logros,
              "experiencia",
              "descripcion"
            );
          }
        }
      });
    }

    // 4. Educación
    if (structuredData.educacion && structuredData.educacion.length > 0) {
      addFragment("Formación Académica", "educacion", "header", 1, true);

      structuredData.educacion.forEach((edu) => {
        let tituloEdu = "";

        if (edu.titulo) {
          tituloEdu = edu.titulo;
        }

        if (edu.institucion) {
          tituloEdu += tituloEdu ? ` - ${edu.institucion}` : edu.institucion;
        }

        addFragment(tituloEdu, "educacion", "titulo", 1, true);

        if (edu.campo_estudio) {
          addFragment(
            `Campo de estudio: ${edu.campo_estudio}`,
            "educacion",
            "campo"
          );
        }

        if (edu.fecha_inicio || edu.fecha_fin) {
          const periodoEdu = `${edu.fecha_inicio || ""} - ${edu.fecha_fin || "Actual"
            }`;
          addFragment(`Período: ${periodoEdu}`, "educacion", "periodo");
        }
      });
    }

    // 5. Aptitudes/Habilidades
    if (structuredData.aptitudes && structuredData.aptitudes.length > 0) {
      addFragment("Habilidades", "habilidades", "header", 1, true);

      // Agrupar habilidades en bloques para no tener demasiados fragmentos pequeños
      const skillGroups = [];
      for (let i = 0; i < structuredData.aptitudes.length; i += 5) {
        skillGroups.push(structuredData.aptitudes.slice(i, i + 5).join(", "));
      }

      skillGroups.forEach((skillGroup) => {
        addFragment(skillGroup, "habilidades", "aptitudes");
      });
    }

    // 6. Certificaciones
    if (
      structuredData.certificaciones &&
      structuredData.certificaciones.length > 0
    ) {
      addFragment("Certificaciones", "certificaciones", "header", 1, true);

      structuredData.certificaciones.forEach((cert) => {
        addFragment(cert, "certificaciones", "certificacion");
      });
    }

    // 7. Idiomas
    if (structuredData.idiomas && structuredData.idiomas.length > 0) {
      addFragment("Idiomas", "idiomas", "header", 1, true);

      structuredData.idiomas.forEach((idioma) => {
        addFragment(idioma, "idiomas", "idioma");
      });
    }

    // 8. Años de experiencia como dato clave (ahora es el punto 8 en vez del 7)
    if (structuredData.anios_experiencia_total) {
      addFragment(
        `Años de experiencia: ${structuredData.anios_experiencia_total}`,
        "datos_clave",
        "experiencia_total"
      );
    }

    console.log(
      `Generados ${fragments.length} fragmentos estructurados a partir del análisis IA`
    );

    return fragments;
  };

  // Mejorar la detección de secciones
  const detectCVSections = (text) => {
    // Patrones ampliados para detectar secciones comunes en un CV
    const sectionPatterns = [
      {
        pattern:
          /\b(EXPERIENCIA|EXPERIENCIA LABORAL|EXPERIENCIA PROFESIONAL)\b/i,
        type: "experiencia",
      },
      {
        pattern: /\b(FORMACIÓN|FORMACION|EDUCACIÓN|EDUCACION|ESTUDIOS)\b/i,
        type: "educacion",
      },
      {
        pattern: /\b(HABILIDADES|APTITUDES|COMPETENCIAS|SKILLS)\b/i,
        type: "habilidades",
      },
      { pattern: /\b(IDIOMAS|LENGUAJES)\b/i, type: "idiomas" },
      {
        pattern:
          /\b(CONOCIMIENTOS|CONOCIMIENTOS TÉCNICOS|CONOCIMIENTOS INFORMATICOS)\b/i,
        type: "conocimientos",
      },
      {
        pattern: /\b(INFORMACIÓN PERSONAL|DATOS PERSONALES|PERFIL)\b/i,
        type: "datos_personales",
      },
      { pattern: /\b(LOGROS|RESULTADOS|ACHIEVEMENTS)\b/i, type: "logros" },
      {
        pattern: /\b(CERTIFICACIONES|CERTIFICADOS|CURSOS)\b/i,
        type: "certificaciones",
      },
      { pattern: /\b(PROYECTOS|TRABAJOS REALIZADOS)\b/i, type: "proyectos" },
      { pattern: /\b(REFERENCIAS|RECOMENDACIONES)\b/i, type: "referencias" },
      // Agregar secciones adicionales comunes en CV
      {
        pattern: /\b(EXTRACTO|RESUMEN|PERFIL PROFESIONAL|OBJETIVO|SUMMARY)\b/i,
        type: "extracto",
      },
      {
        pattern: /\b(CONTACTO|CONTACT|DETALLES DE CONTACTO)\b/i,
        type: "contacto",
      },
    ];

    // Dividir el texto en líneas
    const lines = text.split("\n");
    const structuredLines = [];
    let currentSection = "general";

    // Primera detección de secciones - formato explícito
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Saltar líneas vacías
      if (!line) {
        structuredLines.push(line);
        continue;
      }

      // Verificar si es un encabezado de sección
      let isSectionHeader = false;
      for (const section of sectionPatterns) {
        // Mejorar detección con regex que considera líneas completas y palabras clave exactas
        if (
          (section.pattern.test(line) && line.length < 60) ||
          line.toUpperCase() === section.type.toUpperCase()
        ) {
          currentSection = section.type;
          structuredLines.push(`###SECTION###${section.type}### ${line}`);
          isSectionHeader = true;
          break;
        }
      }

      // Detección de encabezados implícitos (basados en formato)
      if (!isSectionHeader) {
        // Detectar nombre y título al inicio del CV (primeras líneas)
        if (
          i < 3 &&
          /^[A-Z][a-zÁÉÍÓÚáéíóúÑñ\s]{2,40}$/.test(line) &&
          currentSection === "general"
        ) {
          // Probablemente es el nombre del candidato
          structuredLines.push(`###SECTION###datos_personales### ${line}`);
          currentSection = "datos_personales";
          isSectionHeader = true;
        }
        // Detectar "Extracto" o secciones de resumen
        else if (
          i < 10 &&
          line.toLowerCase() === "extracto" &&
          currentSection === "general"
        ) {
          structuredLines.push(`###SECTION###extracto### ${line}`);
          currentSection = "extracto";
          isSectionHeader = true;
        }
        // Detectar sección de experiencia basada en formato (empresa + cargo + fechas)
        else if (
          i > 5 &&
          /^\s*[A-ZÁÉÍÓÚÑ][a-záéíóúñA-ZÁÉÍÓÚÑ\s,\.]{2,50}$/.test(line) &&
          i + 2 < lines.length &&
          /\b(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+\d{4}\b/i.test(
            lines[i + 2]
          )
        ) {
          structuredLines.push(`###SECTION###experiencia### ${line}`);
          currentSection = "experiencia";
          isSectionHeader = true;
        }
      }

      // Si no es un encabezado, preservar la línea con contexto de sección
      if (!isSectionHeader) {
        // Detección de patrón de experiencia laboral
        if (currentSection === "experiencia") {
          // Detectar cargo/posición
          if (
            /^[A-ZÁÉÍÓÚÑ][a-záéíóúñA-ZÁÉÍÓÚÑ\s]{2,50}$/.test(line) &&
            line.length < 60
          ) {
            structuredLines.push(`###POSITION### ${line}`);
          }
          // Detectar fechas o periodos
          else if (
            /\b(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+\d{4}\b/i.test(
              line
            ) ||
            /\b\d{4}\b\s*-\s*(\d{4}|presente|actualidad)/i.test(line) ||
            /\(\s*\d+\s*(año|mes|día|semana)s?\s*\d*\s*(mes|día|semana)?s?\s*\)/i.test(
              line
            )
          ) {
            structuredLines.push(`###PERIOD### ${line}`);
          }
          // Detectar empresa
          else if (
            line.endsWith("S.A.") ||
            line.endsWith("S.A.C.") ||
            /\b(empresa|compañía|corporación)\b/i.test(line)
          ) {
            structuredLines.push(`###ORGANIZATION### ${line}`);
          }
          // Texto normal de experiencia
          else {
            structuredLines.push(`###EXPERIENCIA### ${line}`);
          }
        }
        // Detectar elementos de sección Extracto/Resumen
        else if (currentSection === "extracto") {
          structuredLines.push(`###EXTRACTO### ${line}`);
        }
        // Detectar elementos de datos personales
        else if (currentSection === "datos_personales") {
          // Email
          if (/@/.test(line)) {
            structuredLines.push(`###EMAIL### ${line}`);
          }
          // Teléfono
          else if (/^\+?[\d\s\(\)-]{7,15}$/.test(line)) {
            structuredLines.push(`###PHONE### ${line}`);
          }
          // Ubicación
          else if (/\b(perú|peru|lima|arequipa|cusco|trujillo)\b/i.test(line)) {
            structuredLines.push(`###LOCATION### ${line}`);
          }
          // Título profesional
          else if (
            /\b(ingeniero|licenciado|técnico|especialista|analista|gerente|jefe|director)\b/i.test(
              line
            )
          ) {
            structuredLines.push(`###TITLE### ${line}`);
          } else {
            structuredLines.push(`###DATOS_PERSONALES### ${line}`);
          }
        }
        // Texto genérico para otras secciones
        else {
          structuredLines.push(`###${currentSection.toUpperCase()}### ${line}`);
        }
      }
    }

    // Convertir a texto estructurado
    return structuredLines.join("\n");
  };

  // Mejorar la creación de fragmentos para asegurar que se capture toda la información
  const createCVFragments = (structuredText, pageNumber) => {
    console.log(
      `===== CÓMO LLEGA EL TEXTO A createCVFragments (pág ${pageNumber}) =====`
    );
    console.log(structuredText);
    console.log(`=========================================================`);

    // Fragmentar considerando la página pero evitando dividir párrafos cohesivos
    const isFirstPage = pageNumber === 1;
    const fragmentMaxLength = isFirstPage ? 1000 : maxFragmentLength; // Aumentar para primera página

    const fragments = [];
    let currentSection = "general";
    let currentSectionHeader = null;

    // PASO 1: Primero unificar líneas que formen un mismo párrafo semántico
    // Dividir inicialmente por líneas para análisis
    const lines = structuredText.split("\n");
    const mergedBlocks = [];
    let currentBlock = "";
    let currentContext = null;

    console.log(`Procesando ${lines.length} líneas en la página ${pageNumber}`);

    // Agrupar líneas basándose en reglas semánticas
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Saltar líneas vacías

      // Detectar cambios de sección o contexto
      if (line.includes("###SECTION###")) {
        // Guardar bloque anterior y comenzar uno nuevo con el encabezado
        if (currentBlock) {
          mergedBlocks.push({
            text: currentBlock,
            context: currentContext || "general",
            isHeader: false,
          });
        }

        // Actualizar contexto con nueva sección
        const match = line.match(/###SECTION###([^#]+)###/);
        currentContext = match ? match[1].trim() : null;
        currentBlock = line;

        // Este bloque es un encabezado de sección
        mergedBlocks.push({
          text: currentBlock,
          context: currentContext,
          isHeader: true,
        });
        currentBlock = "";
      } else if (line.match(/###[A-Z_]+###/)) {
        // Guardar bloque anterior si existe
        if (currentBlock) {
          mergedBlocks.push({
            text: currentBlock,
            context: currentContext,
            isHeader: false,
          });
        }

        // Extraer el contexto específico de este marcador
        const contextMatch = line.match(/###([A-Z_]+)###/);
        const lineContext = contextMatch
          ? contextMatch[1].toLowerCase()
          : currentContext;

        // Crear un nuevo bloque con este contexto específico
        currentBlock = line;
        mergedBlocks.push({
          text: currentBlock,
          context: lineContext,
          isHeader: false,
          specificContext: lineContext,
        });
        currentBlock = "";
      } else {
        // REGLAS DE AGRUPACIÓN para líneas normales:
        const shouldContinueParagraph =
          currentBlock &&
          // Caso 1: La línea actual comienza con minúscula
          (!line[0] ||
            /[a-záéíóúñ]/.test(line[0]) ||
            // Caso 2: La línea anterior no termina con signo que indica fin (., !, ?)
            (!currentBlock.trim().endsWith(".") &&
              !currentBlock.trim().endsWith("!") &&
              !currentBlock.trim().endsWith("?")) ||
            // Caso 3: El texto encaja con patrones de continuación (ej: conectores)
            /^(y|e|o|u|ni|que|como|donde|cuando|si|pero|porque|pues|además)\b/i.test(
              line
            ) ||
            // Caso 4: Estamos en un contexto que típicamente contiene párrafos multi-línea
            [
              "extracto",
              "experiencia",
              "proyectos",
              "habilidades",
              "conocimientos",
            ].includes(currentContext) ||
            // Caso 5: Líneas muy cortas que probablemente continúan la anterior
            (line.length < 40 && !line.match(/^\s*[\-•·]/)));

        if (shouldContinueParagraph) {
          // Continuar el párrafo actual
          if (currentBlock) {
            currentBlock += " " + line;
          } else {
            currentBlock = line;
          }
        } else {
          // Comenzar un nuevo párrafo
          if (currentBlock) {
            mergedBlocks.push({
              text: currentBlock,
              context: currentContext,
              isHeader: false,
            });
          }
          currentBlock = line;
        }
      }
    }

    // No olvidar el último bloque
    if (currentBlock) {
      mergedBlocks.push({
        text: currentBlock,
        context: currentContext,
        isHeader: false,
      });
    }

    console.log(
      `Generados ${mergedBlocks.length} bloques semánticos (antes eran ${lines.length} líneas)`
    );

    // PASO 2: Procesar cada bloque semántico para crear fragmentos finales
    mergedBlocks.forEach((block, blockIdx) => {
      const { text, context, isHeader, specificContext } = block;

      // Si es un encabezado de sección, actualizar estado y crear fragmento
      if (isHeader && text.includes("###SECTION###")) {
        const sectionMatch = text.match(/###SECTION###([^#]+)###\s*(.*)/);
        if (sectionMatch) {
          currentSection = sectionMatch[1].trim();
          currentSectionHeader = sectionMatch[2].trim();

          fragments.push({
            text: currentSectionHeader,
            page: pageNumber,
            paragraph_index: fragments.length,
            section: currentSection,
            section_header: currentSectionHeader,
            is_section_header: true,
          });
        }
      } else {
        // Procesar contenido normal
        if (text.length <= fragmentMaxLength) {
          // Limpiar los marcadores de contexto
          let cleanedText = text;
          let extractedContext = specificContext || context;

          // Si hay marcador de contexto específico, extraerlo
          if (!specificContext && text.match(/###([A-Z_]+)###/)) {
            const match = text.match(/###([A-Z_]+)###\s*(.*)/);
            if (match) {
              extractedContext = match[1].toLowerCase();
              cleanedText = match[2] || "";
            }
          } else {
            // Limpiar cualquier marcador de contexto
            cleanedText = text.replace(/###[A-Z_]+###\s*/g, "");
          }

          fragments.push({
            text: cleanText(cleanedText),
            page: pageNumber,
            paragraph_index: fragments.length,
            section: currentSection,
            section_header: currentSectionHeader,
            context: extractedContext,
          });
        } else {
          // Para textos muy largos, dividirlos pero preservando sentido
          const subFragments = splitLongParagraph(text, fragmentMaxLength);

          subFragments.forEach((fragment, sfIdx) => {
            // Extraer contexto si existe
            let fragmentContext = context;
            let fragmentContent = fragment;

            const contextMatch = fragment.match(/###([A-Z_]+)###\s*(.*)/);
            if (contextMatch) {
              fragmentContext = contextMatch[1].toLowerCase();
              fragmentContent = contextMatch[2] || "";
            } else {
              fragmentContent = fragment.replace(/###[A-Z_]+###\s*/g, "");
            }

            fragments.push({
              text: cleanText(fragmentContent),
              page: pageNumber,
              paragraph_index: fragments.length,
              section: currentSection,
              section_header: currentSectionHeader,
              context: fragmentContext,
              is_partial: subFragments.length > 1,
              part_index: sfIdx + 1,
              total_parts: subFragments.length,
            });
          });
        }
      }
    });

    console.log(
      `Página ${pageNumber}: Generados ${fragments.length} fragmentos finales`
    );

    return fragments;
  };

  // Subir archivo a Firebase Storage
  const uploadToFirebase = async (file, uniqueId) => {
    if (!file) return null;

    try {
      setUploading(true);
      setError(null);
      setSuccess(`Subiendo archivo ${file.name}...`);

      const storage = getStorage(firebaseApp);
      const fileName = `hiro_cv/${uniqueId}/${file.name}`;
      const fileRef = ref(storage, fileName);

      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);

      setFileUrl(downloadUrl);
      setSuccess(`Archivo ${file.name} subido correctamente`);

      return {
        url: downloadUrl,
        storagePath: fileName,
        fileBuffer: file, // Devolver también el archivo para procesamiento local
      };
    } catch (error) {
      console.error("Error al subir archivo:", error);
      setError(`Error al subir archivo: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Extraer texto del PDF - optimizado para CV

  const extractCVText = async (file, uniqueId) => {
    if (!pdfJsLoaded || !window.pdfjsLib) {
      setError(
        "La biblioteca PDF.js no está cargada aún. Por favor, espere unos segundos y vuelva a intentarlo."
      );
      return null;
    }

    try {
      setExtracting(true);
      setError(null);
      setSuccess(`Extrayendo texto del currículum ${file.name}...`);

      // Código existente para extraer texto...
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        cMapUrl: "https://unpkg.com/pdfjs-dist@3.11.174/cmaps/",
        cMapPacked: true,
      }).promise;

      let fullText = "";
      const pageTexts = [];

      // Metadatos del CV
      const cvMetadata = {
        title: file.name.replace(/\.pdf$/, ""),
        description: docDescription || "Currículum Vitae",
        pageCount: pdf.numPages,
        extractionDate: new Date().toISOString(),
        documentId: uniqueId,
        sections: {},
        contactInfo: {},
      };

      // Extraer texto de cada página
      for (let i = 1; i <= pdf.numPages; i++) {
        setSuccess(`Procesando página ${i} de ${pdf.numPages}...`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        // Extraer texto con formato optimizado para CV
        const pageText = extractCVPageText(textContent);

        // Mostrar texto original para debugging
        console.log(`===== TEXTO ORIGINAL PÁGINA ${i} =====`);
        console.log(pageText);
        console.log(`====================================`);

        pageTexts.push({
          pageNumber: i,
          text: pageText,
        });

        fullText += pageText + "\n\n";

        // Intentar extraer información de contacto (email, teléfono) de la primera página
        // Este código puede mantenerse como respaldo
        if (i === 1) {
          extractContactInfo(pageText, cvMetadata);
        }
      }

      // Limpiar el texto completo
      fullText = cleanText(fullText);

      // NUEVO: Analizar el CV con IA para obtener estructura
      let fragments = [];
      let analyzedWithAI = false;

      setSuccess("Analizando el currículum con IA para mejor estructura...");
      const aiAnalysisResult = await analyzeCV(fullText, file.name, uniqueId);

      if (aiAnalysisResult.success && aiAnalysisResult.data) {
        console.log("Análisis IA exitoso:", aiAnalysisResult.data);

        // Crear fragmentos a partir de la estructura IA
        fragments = createFragmentsFromStructuredData(
          aiAnalysisResult.data,
          uniqueId,
          pdf.numPages
        );

        // Actualizar metadatos con la información de IA
        cvMetadata.aiAnalysis = aiAnalysisResult.data;
        cvMetadata.contactInfo = {
          name:
            aiAnalysisResult.data.informacion_personal?.nombre_completo || "",
          email:
            typeof aiAnalysisResult.data.informacion_personal?.contacto ===
              "object"
              ? aiAnalysisResult.data.informacion_personal?.contacto.email || ""
              : "",
          phone:
            typeof aiAnalysisResult.data.informacion_personal?.contacto ===
              "object"
              ? aiAnalysisResult.data.informacion_personal?.contacto.teléfono ||
              ""
              : "",
          location: aiAnalysisResult.data.informacion_personal?.ubicacion || "",
        };

        // Meter otros datos útiles
        cvMetadata.yearsOfExperience =
          aiAnalysisResult.data.anios_experiencia_total || null;

        // Indicar que se usó IA
        analyzedWithAI = true;
        if (aiAnalysisResult.summary) {
          cvMetadata.summaryStructured = aiAnalysisResult.summary;
        }
      } else {
        console.log("No se pudo analizar con IA, usando método tradicional");

        // Código existente para usar método tradicional
        const traditionalFragments = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const pageText = pageTexts[i - 1].text;
          const structuredText = detectSections
            ? detectCVSections(pageText)
            : pageText;
          const pageFragments = createCVFragments(structuredText, i);
          traditionalFragments.push(...pageFragments);
        }

        fragments = traditionalFragments;
      }

      // Completar los fragmentos con IDs y metadatos
      const enhancedFragments = fragments.map((fragment, index) => {
        // Asegurar que todos los fragmentos tengan un ID correcto
        const fragmentId = fragment.fragment_id || `${uniqueId}_f${index}`;

        return {
          ...fragment,
          id: fragmentId,
          fragment_id: fragmentId,
          document_id: uniqueId,
          extraction_date: cvMetadata.extractionDate,
          char_count: fragment.text ? fragment.text.length : 0,
        };
      });

      // Actualizar estado para el archivo actual
      if (!processingAll) {
        setResult({
          success: true,
          message: analyzedWithAI
            ? `Texto extraído y estructurado con IA correctamente`
            : `Texto extraído correctamente de ${pdf.numPages} páginas`,
          text: fullText,
          fragmentCount: enhancedFragments.length,
          pageTexts: pageTexts,
          metadata: cvMetadata,
          pageCount: pdf.numPages,
          analyzedWithAI: analyzedWithAI,
        });
        setTextFragments(enhancedFragments);
      }

      setSuccess(
        `Texto extraído correctamente de ${file.name}: ${enhancedFragments.length} fragmentos`
      );

      return {
        text: fullText,
        fragments: enhancedFragments,
        pageCount: pdf.numPages,
        pageTexts,
        metadata: cvMetadata,
        analyzedWithAI: analyzedWithAI,
      };
    } catch (error) {
      console.error("Error al extraer texto:", error);
      setError(
        `Error al extraer texto de ${file.name}: ${error.message || "Error desconocido"
        }`
      );
      return null;
    } finally {
      setExtracting(false);
    }
  };

  // Función para extraer información de contacto
  const extractContactInfo = (text, metadata) => {
    // Extraer email
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
    const emailMatch = text.match(emailRegex);
    if (emailMatch) {
      metadata.contactInfo.email = emailMatch[0];
    }

    // Extraer teléfono (varios formatos)
    const phoneRegex =
      /\b(?:\+?[0-9]{1,3}[-.\s]?)?(?:\(?[0-9]{1,4}\)?[-.\s]?)?[0-9]{3}[-.\s]?[0-9]{4}\b/;
    const phoneMatch = text.match(phoneRegex);
    if (phoneMatch) {
      metadata.contactInfo.phone = phoneMatch[0];
    }

    // Intentar extraer nombre (suele estar al inicio del CV, primera línea larga)
    const lines = text.split("\n");
    for (const line of lines) {
      if (
        line.length > 5 &&
        line.length < 60 &&
        /^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+/.test(line)
      ) {
        metadata.contactInfo.name = line.trim();
        break;
      }
    }
  };

  // Función para indexar los fragmentos en Firestore (optimizada para CV)
  const indexCVFragmentsToFirestore = async (
    storagePath,
    enhancedFragments,
    metadata,
    uniqueId,
    fileUrl
  ) => {
    if (!uniqueId) {
      setError("No se pudo generar un ID para el currículum");
      return false;
    }

    try {
      setIndexing(true);
      setError(null);
      setIndexProgress(0);
      setSuccess("Preparando la indexación de fragmentos...");

      // Usar los fragmentos generados
      const fragments = enhancedFragments;

      if (!fragments || fragments.length === 0) {
        setError("No hay fragmentos de texto para indexar");
        return false;
      }

      // 1. Crear documento principal en la colección "hiro_documentos"
      setSuccess("Creando documento principal...");
      const documentData = {
        document_id: uniqueId,
        title: metadata.title || "Currículum sin título",
        description: metadata.description || "Currículum Vitae",
        source: "PDF",
        source_filename: metadata.title + ".pdf",
        creation_date: new Date().toISOString(),
        page_count: metadata.pageCount || 0,
        fragment_count: fragments.length,
        status: "active",
        document_type: "curriculum",
        category: "reclutamiento",
        storage_path: storagePath || "",
        last_indexed: new Date().toISOString(),
        url: fileUrl,
        ruc: ruc,
        vacante_id: vacanteId || null,
        contact_info: metadata.contactInfo || {},
      };

      await setDoc(doc(db, "hiro_documentos", uniqueId), documentData, { merge: true });
      setIndexProgress(10);

      // 2. Guardar los fragmentos en lotes para mejor rendimiento
      setSuccess("Indexando fragmentos...");
      const batchSize = 20;
      let batch = writeBatch(db);
      let batchCount = 0;
      let processedCount = 0;

      // Crear colección de fragmentos
      for (const fragment of fragments) {
        // Crear un ID único para este fragmento en Firestore
        const fragmentRef = doc(collection(db, "hiro_fragmentos"));

        // Preparar datos para Firestore
        const fragmentData = {
          fragment_id: fragment.fragment_id || fragment.id,
          document_id: uniqueId,
          page: fragment.page,
          paragraph_index: fragment.paragraph_index,
          text: fragment.text,
          char_count: fragment.text.length,

          // Campos específicos para CV
          section: fragment.section || "general",
          section_header: fragment.section_header || null,
          context: fragment.context || null,
          is_section_header: fragment.is_section_header || false,

          // Campos para fragmentos parciales
          is_partial: fragment.is_partial || false,
          part_index: fragment.part_index || null,
          total_parts: fragment.total_parts || null,

          // Metadatos adicionales
          extraction_date: new Date().toISOString(),
          embedding_id: null,
          document_type: "curriculum",
          category: "reclutamiento",
          ruc: ruc,
          vacante_id: vacanteId || null,
        };

        // Añadir documento al batch
        batch.set(fragmentRef, fragmentData);
        batchCount++;

        // Si alcanzamos el tamaño del lote, enviarlo
        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
          processedCount += batchSize;
          setIndexProgress(
            Math.min(
              90,
              10 + Math.round((processedCount / fragments.length) * 80)
            )
          );
          setSuccess(
            `Indexando fragmentos... ${Math.round(
              (processedCount / fragments.length) * 100
            )}%`
          );
        }
      }

      // Enviar el último lote si hay documentos pendientes
      if (batchCount > 0) {
        await batch.commit();
        processedCount += batchCount;
        setIndexProgress(95);
      }

      // 3. Guardar información de secciones del CV si se detectaron
      if (metadata.sections && Object.keys(metadata.sections).length > 0) {
        setSuccess("Guardando estructura del currículum...");
        const sectionsBatch = writeBatch(db);

        for (const [sectionKey, sectionInfo] of Object.entries(
          metadata.sections
        )) {
          const sectionId = `${uniqueId}_${sectionKey}`;
          const sectionRef = doc(
            collection(db, "hiro_cv_secciones"),
            sectionId
          );

          sectionsBatch.set(sectionRef, {
            document_id: uniqueId,
            section_id: sectionId,
            section_type: sectionKey,
            title: sectionInfo.title,
            content: sectionInfo.content || "",
            page: sectionInfo.page || 1,
            extraction_date: new Date().toISOString(),
            ruc: ruc,
          });
        }

        await sectionsBatch.commit();
      }

      // Actualizamos el estado después de una indexación exitosa
      setIndexProgress(100);
      setSuccess(`Currículum ${metadata.title} indexado correctamente`);

      if (!processingAll) {
        setResult((prev) => ({
          ...prev,
          metadata,
          indexedToFirestore: true,
          message: `Currículum indexado correctamente con ${fragments.length} fragmentos`,
        }));
      }

      return {
        success: true,
        uniqueId,
        title: metadata.title,
        fragmentCount: fragments.length,
        message: `Indexación completada con ${fragments.length} fragmentos`,
      };
    } catch (error) {
      console.error("Error al indexar en Firestore:", error);
      setError(`Error al indexar en Firestore: ${error.message}`);

      return {
        success: false,
        uniqueId,
        title: metadata.title,
        message: `Error: ${error.message}`,
      };
    } finally {
      setIndexing(false);
    }
  };

  // Función para procesar embeddings - específica para currículums
  const processCVEmbeddings = async (documentId) => {
    if (!documentId) {
      setError("Se requiere un ID de documento para generar embeddings");
      return false;
    }

    try {
      // Actualizar estado
      setProcessingEmbeddings(true);
      setEmbeddingsProgress(0);
      setProcessingStatus("Generando embeddings para el currículum...");

      // URL de la Cloud Function
      const functionUrl = FUNCTION_URLS.hiroProcessFragmentEmbeddings;

      // Configuración
      let continueProcessing = true;
      let totalProcessed = 0;
      let retryCount = 0;
      const maxRetries = 3;
      const batchSize = 50;

      // Procesamiento por lotes
      while (continueProcessing && retryCount < maxRetries) {
        try {
          const response = await axios.post(functionUrl, {
            documentId: documentId,
            batchSize: batchSize,
            namespace: "cv", // Namespace específico para currículums
            ruc: ruc,
          });

          const data = response.data;

          if (data.success) {
            if (data.processed > 0) {
              totalProcessed += data.processed;
              setEmbeddingsProgress((prev) => Math.min(95, prev + 10));
              setProcessingStatus(
                `Embeddings generados para ${totalProcessed} fragmentos del currículum`
              );
            } else {
              continueProcessing = false;
              setEmbeddingsProgress(100);
              setProcessingStatus(
                `Procesamiento completo. ${totalProcessed} fragmentos del currículum con embeddings generados.`
              );
            }
          } else {
            if (data.code === "EMBEDDING_QUOTA_EXCEEDED") {
              continueProcessing = false;
              setEmbeddingsProgress(100);
              setProcessingStatus(
                `Embeddings en pausa por cuota del proveedor. Procesados hasta ahora: ${totalProcessed}.`
              );
              break;
            }
            if (
              data.message &&
              data.message.includes("No se encontraron fragmentos pendientes")
            ) {
              console.log("No hay fragmentos pendientes de procesar");
              continueProcessing = false;
              setEmbeddingsProgress(100);

              if (totalProcessed === 0) {
                setProcessingStatus(
                  "Todos los fragmentos ya tienen embeddings generados."
                );
              } else {
                setProcessingStatus(
                  `Procesamiento completo. ${totalProcessed} fragmentos con embeddings generados.`
                );
              }
            } else {
              throw new Error(
                data.message ||
                "Error desconocido en el procesamiento de embeddings"
              );
            }
          }
        } catch (error) {
          if (
            error.message &&
            error.message.includes("No se encontraron fragmentos pendientes")
          ) {
            continueProcessing = false;
            setEmbeddingsProgress(100);

            if (totalProcessed === 0) {
              setProcessingStatus(
                "Todos los fragmentos ya tienen embeddings generados."
              );
            } else {
              setProcessingStatus(
                `Procesamiento completo. ${totalProcessed} fragmentos con embeddings generados.`
              );
            }
            break;
          }

          retryCount++;
          console.error(`Error en intento ${retryCount}:`, error);

          if (retryCount >= maxRetries) {
            throw new Error(
              `Error después de ${maxRetries} intentos: ${error.message}`
            );
          }

          await new Promise((resolve) =>
            setTimeout(resolve, 2000 * Math.pow(2, retryCount - 1))
          );
        }
      }

      // Verificar el estado final
      if (!processingAll) {
        showNotification(
          "Embeddings completados",
          `Se han generado embeddings para ${totalProcessed} fragmentos del currículum.`
        );

        // Actualizar el resultado con información de embeddings
        setResult((prev) => ({
          ...prev,
          embeddingsGenerated: true,
          embeddingsCount: totalProcessed,
        }));
      }

      return {
        success: true,
        documentId,
        embeddingsCount: totalProcessed,
      };
    } catch (error) {
      console.error("Error al procesar embeddings:", error);
      setError(`Error al generar embeddings: ${error.message}`);

      if (!processingAll) {
        showNotification(
          "Error en generación de embeddings",
          `No se pudieron procesar todos los embeddings: ${error.message}`,
          "error"
        );
      }

      return {
        success: false,
        documentId,
        message: error.message,
      };
    } finally {
      setProcessingEmbeddings(false);
    }
  };

  const processStructuredCvEmbedding = async ({
    summary,
    fullText,
    documentId,
    metadata,
  }) => {
    if (!summary || !documentId) {
      return { success: false, message: "Falta summary/documentId" };
    }
    const functionUrl = FUNCTION_URLS.hiroProcessSummaryEmbedding;
    const response = await axios.post(functionUrl, {
      summary,
      fullText,
      documentId,
      metadata,
      ruc,
      telefono: metadata?.contactInfo?.phone || null,
      procesoId: null,
    });
    return response.data || { success: true };
  };

  // Función para dividir párrafos largos en fragmentos más pequeños
  const splitLongParagraph = (paragraph, maxLength) => {
    if (paragraph.length <= maxLength) return [paragraph];

    const fragments = [];
    let start = 0;

    // División por longitud con preservación de contexto
    let contextPrefix = "";

    // Detectar si hay un prefijo de contexto
    if (paragraph.match(/###[A-Z_]+###/)) {
      const contextMatch = paragraph.match(/(###[A-Z_]+###\s*)/);
      if (contextMatch) {
        contextPrefix = contextMatch[1];
        paragraph = paragraph.substring(contextMatch[0].length);
      }
    }

    // Dividir el texto
    while (start < paragraph.length) {
      let end = Math.min(start + maxLength, paragraph.length);

      // Buscar un buen punto de corte
      if (end < paragraph.length) {
        const cutPoints = [
          paragraph.lastIndexOf(". ", end),
          paragraph.lastIndexOf("? ", end),
          paragraph.lastIndexOf("! ", end),
          paragraph.lastIndexOf("\n", end),
          paragraph.lastIndexOf(";", end),
          paragraph.lastIndexOf(",", end),
        ].filter((pos) => pos > start);

        if (cutPoints.length > 0) {
          end = Math.max(...cutPoints) + 1;
        } else {
          const lastSpace = paragraph.lastIndexOf(" ", end);
          if (lastSpace > start) {
            end = lastSpace + 1;
          }
        }
      }

      const fragment = paragraph.substring(start, end).trim();
      if (fragment) {
        fragments.push(contextPrefix + fragment);
      }

      start = end;
    }

    return fragments;
  };

  // Función para limpiar texto - adaptada para CVs
  const cleanText = (text) => {
    if (!text) return "";

    // Limpiar texto sin eliminar estructura importante
    return text
      .replace(/\s+/g, " ") // Normalizar espacios consecutivos
      .replace(/\s+\./g, ".") // Quitar espacios antes de puntos
      .replace(/\s+,/g, ",") // Quitar espacios antes de comas
      .replace(/^\s+|\s+$/g, "") // Quitar espacios al principio y final
      .replace(/ {2,}/g, " ") // Reducir múltiples espacios a uno
      .replace(/###[A-Z_]+###\s*/g, "") // Eliminar marcadores de contexto
      .trim();
  };

  // Nueva función para procesar un solo archivo
  const processSingleFile = async (file, index) => {
    try {
      // Generar un ID único para este currículum
      const timestamp = new Date().getTime();
      const uniqueId = `cv_${timestamp}_${Math.floor(Math.random() * 1000)}`;

      // Mostrar progreso general
      setSuccess(
        `Procesando archivo ${index + 1} de ${files.length}: ${file.name}`
      );

      // 1. Subir el archivo a Firebase Storage
      const uploadResult = await uploadToFirebase(file, uniqueId);
      if (!uploadResult) {
        return {
          success: false,
          fileName: file.name,
          message: "Error al subir el currículum",
        };
      }

      // 2. Extraer el texto del CV
      const extractResult = await extractCVText(file, uniqueId);
      if (!extractResult) {
        return {
          success: false,
          fileName: file.name,
          message: "Error al extraer el texto del currículum",
        };
      }

      // 3. Indexar los fragmentos en Firestore
      const indexResult = await indexCVFragmentsToFirestore(
        uploadResult.storagePath,
        extractResult.fragments,
        extractResult.metadata,
        uniqueId,
        uploadResult.url
      );

      // 4. Estrategia Hiro: 1 embedding estructurado por CV (sin embeddings por fragmento)
      if (indexResult.success && procesarDatos) {
        const summary = extractResult?.metadata?.summaryStructured || null;
        if (summary) {
          const oneEmbeddingResult = await processStructuredCvEmbedding({
            summary,
            fullText: extractResult.text,
            documentId: uniqueId,
            metadata: {
              title: extractResult?.metadata?.title || file.name,
              description: extractResult?.metadata?.description || "Currículum Vitae",
              pageCount: extractResult?.metadata?.pageCount || 0,
              sourceFilename: file.name,
              candidate_name: extractResult?.metadata?.contactInfo?.name || "Sin nombre",
              vacanteId: vacanteId || null,
              document_type: "cv",
              category: "reclutamiento",
              storage_path: uploadResult.storagePath || "",
            },
          });
          indexResult.embeddingsGenerated = Boolean(oneEmbeddingResult?.success !== false);
          indexResult.embeddingsCount = indexResult.embeddingsGenerated ? 1 : 0;
        } else {
          indexResult.embeddingsGenerated = false;
          indexResult.embeddingsCount = 0;
          indexResult.message = `${indexResult.message} | Sin summary estructurado para embedding único`;
        }
      }

      indexResult.fileName = file.name;
      return indexResult;
    } catch (error) {
      console.error(
        `Error en el procesamiento del currículum ${file.name}:`,
        error
      );
      return {
        success: false,
        fileName: file.name,
        message: `Error: ${error.message}`,
      };
    }
  };

  // Función para procesar un único currículum
  const processCurriculum = async () => {
    if (files.length === 0) {
      setError("No se ha seleccionado ningún archivo PDF");
      return;
    }

    try {
      // Procesar el currículum actual
      const currentFile = files[currentFileIndex];
      const result = await processSingleFile(currentFile, currentFileIndex);

      // Actualizar resultados
      setProcessingResults((prev) => [...prev, result]);

      // Mostrar notificación
      showNotification(
        result.success ? "Procesamiento completado" : "Error en procesamiento",
        result.message,
        result.success ? "success" : "error"
      );
    } catch (error) {
      console.error("Error general en el procesamiento:", error);
      setError(`Error en el procesamiento: ${error.message}`);

      showNotification(
        "Error en el procesamiento",
        `No se pudo completar el procesamiento: ${error.message}`,
        "error"
      );
    }
  };

  // Nueva función para procesar todos los archivos en lote
  const processAllFiles = async () => {
    if (files.length === 0) {
      setError("No hay archivos seleccionados para procesar");
      return;
    }

    try {
      setProcessingAll(true);
      setTotalProgress(0);
      setSuccess(`Iniciando procesamiento de ${files.length} currículums...`);
      setProcessingResults([]);

      const results = [];

      // Procesar cada archivo secuencialmente
      for (let i = 0; i < files.length; i++) {
        // Actualizar progreso total
        const fileProgress = Math.round((i / files.length) * 100);
        setTotalProgress(fileProgress);

        // Procesar archivo actual
        const result = await processSingleFile(files[i], i);
        results.push(result);

        // Actualizar resultados parciales
        setProcessingResults([...results]);
      }

      // Procesamiento completado
      setTotalProgress(100);
      setSuccess(
        `Procesamiento completo: ${results.filter((r) => r.success).length
        } de ${files.length} currículums procesados correctamente`
      );

      // Mostrar resumen final
      const successCount = results.filter((r) => r.success).length;
      showNotification(
        "Procesamiento por lotes completado",
        `Se han procesado ${successCount} de ${files.length} currículums correctamente`,
        successCount === files.length ? "success" : "info"
      );
    } catch (error) {
      console.error("Error en el procesamiento por lotes:", error);
      setError(`Error en el procesamiento por lotes: ${error.message}`);

      showNotification(
        "Error en procesamiento por lotes",
        `Ocurrió un error inesperado: ${error.message}`,
        "error"
      );
    } finally {
      setProcessingAll(false);
    }
  };

  // Renderizar componente
  return (
    <Container>
      {renderRucInfo()}

      <Card className="mb-4">
        <Card.Header>
          <h5>Extracción de Currículums</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Seleccionar Currículums PDF</Form.Label>
            <Form.Control
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              disabled={uploading || extracting || indexing || processingAll}
              multiple // Permitir selección múltiple
            />
            <Form.Text className="text-muted">
              Seleccione uno o varios archivos PDF de currículum para
              procesarlos.
            </Form.Text>
          </Form.Group>

          {files.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <div className="me-auto">
                  <strong>Archivos seleccionados:</strong> {files.length}{" "}
                  currículums
                </div>
              </div>
              <div
                className="small text-muted mb-3"
                style={{ maxHeight: "100px", overflowY: "auto" }}
              >
                {files.map((file, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-1">
                    <span className="me-2">
                      {idx + 1}. {file.name}
                    </span>
                    <Badge bg="secondary" className="me-1">
                      {(file.size / 1024).toFixed(1)} KB
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <Button
              variant="link"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="p-0 text-decoration-none"
            >
              {showAdvanced
                ? "▼ Ocultar opciones avanzadas"
                : "► Mostrar opciones avanzadas"}
            </Button>
          </div>

          {showAdvanced && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Longitud máxima de fragmento</Form.Label>
                  <Form.Control
                    type="number"
                    value={maxFragmentLength}
                    onChange={(e) =>
                      setMaxFragmentLength(parseInt(e.target.value) || 500)
                    }
                    min="200"
                    max="1000"
                    disabled={
                      uploading || extracting || indexing || processingAll
                    }
                  />
                  <Form.Text className="text-muted">
                    Tamaño recomendado para CV: 500 caracteres
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mt-2">
                  <Form.Check
                    type="checkbox"
                    id="detectSections"
                    label="Detectar secciones del currículum"
                    checked={detectSections}
                    onChange={(e) => setDetectSections(e.target.checked)}
                    disabled={
                      uploading || extracting || indexing || processingAll
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="procesarDatos"
                    label="Generar embeddings automáticamente"
                    checked={procesarDatos}
                    onChange={(e) => setProcesarDatos(e.target.checked)}
                    disabled={
                      uploading || extracting || indexing || processingAll
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <div className="d-flex gap-2">
            {/* Botón de procesamiento individual */}
            {files.length > 0 && (
              <Button
                variant="primary"
                onClick={processCurriculum}
                disabled={
                  files.length === 0 ||
                  !pdfJsLoaded ||
                  uploading ||
                  extracting ||
                  indexing ||
                  processingEmbeddings ||
                  processingAll
                }
              >
                {uploading || extracting || indexing || processingEmbeddings ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Procesando archivo {currentFileIndex + 1}...
                  </>
                ) : (
                  "Procesar CV Actual"
                )}
              </Button>
            )}

            {/* Botón de procesamiento por lotes */}
            {files.length > 1 && (
              <Button
                variant="success"
                onClick={processAllFiles}
                disabled={
                  files.length === 0 ||
                  !pdfJsLoaded ||
                  uploading ||
                  extracting ||
                  indexing ||
                  processingEmbeddings ||
                  processingAll
                }
              >
                {processingAll ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Procesando lote...
                  </>
                ) : (
                  `Procesar Todos (${files.length})`
                )}
              </Button>
            )}

            {/* Selector de archivo cuando hay múltiples */}
            {files.length > 1 && !processingAll && (
              <Form.Select
                value={currentFileIndex}
                onChange={(e) => setCurrentFileIndex(parseInt(e.target.value))}
                disabled={
                  uploading ||
                  extracting ||
                  indexing ||
                  processingEmbeddings ||
                  processingAll
                }
                style={{ maxWidth: "250px" }}
              >
                {files.map((file, idx) => (
                  <option key={idx} value={idx}>
                    {idx + 1}.{" "}
                    {file.name.length > 30
                      ? file.name.substring(0, 27) + "..."
                      : file.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </div>
        </Card.Body>

        {(uploading || extracting || indexing) && !processingAll && (
          <Card.Footer>
            <div className="small text-muted mb-1">
              Progreso del archivo actual
            </div>
            <ProgressBar
              now={indexing ? indexProgress : 100}
              label={`${indexing ? indexProgress : 100}%`}
            />
            {success && <div className="small mt-2">{success}</div>}
          </Card.Footer>
        )}

        {processingAll && (
          <Card.Footer>
            <div className="small text-muted mb-1">
              Progreso total del lote ({files.length} archivos)
            </div>
            <ProgressBar
              now={totalProgress}
              label={`${totalProgress}%`}
              variant="success"
            />
            {success && <div className="small mt-2">{success}</div>}
          </Card.Footer>
        )}

        {processingEmbeddings && !processingAll && (
          <Card.Footer>
            <div className="small text-muted mb-1">Generando embeddings</div>
            <ProgressBar
              now={embeddingsProgress}
              label={`${embeddingsProgress}%`}
              variant="info"
            />
            <div className="small mt-2">{processingStatus}</div>
          </Card.Footer>
        )}
      </Card>

      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {/* Resultados del procesamiento por lotes */}
      {processingResults.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h5>Resultados del Procesamiento</h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Archivo</th>
                  <th>Estado</th>
                  <th>Fragmentos</th>
                  <th>Embeddings</th>
                  <th>Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {processingResults.map((result, idx) => (
                  <tr
                    key={idx}
                    className={
                      result.success ? "table-success" : "table-danger"
                    }
                  >
                    <td>{idx + 1}</td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {result.fileName}
                      </div>
                    </td>
                    <td>
                      <Badge bg={result.success ? "success" : "danger"}>
                        {result.success ? "Éxito" : "Error"}
                      </Badge>
                    </td>
                    <td>{result.fragmentCount || "-"}</td>
                    <td>
                      {result.embeddingsGenerated ? (
                        <Badge bg="info">
                          {result.embeddingsCount || "Generados"}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">No generados</Badge>
                      )}
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "300px" }}
                      >
                        {result.message}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <span>
              Total procesados: <strong>{processingResults.length}</strong> de{" "}
              <strong>{files.length}</strong>
            </span>
            <span>
              Exitosos:{" "}
              <Badge bg="success">
                {processingResults.filter((r) => r.success).length}
              </Badge>{" "}
              Fallidos:{" "}
              <Badge bg="danger">
                {processingResults.filter((r) => !r.success).length}
              </Badge>
            </span>
          </Card.Footer>
        </Card>
      )}

      {/* Resultado individual (solo cuando no se está procesando por lotes) */}
      {result && !processingAll && (
        <Card className="mb-4">
          <Card.Header>
            <h5>Detalle del Procesamiento</h5>
          </Card.Header>
          <Card.Body>
            <Tabs defaultActiveKey="fragments" className="mb-3">
              <Tab
                eventKey="fragments"
                title={`Fragmentos (${textFragments.length})`}
              >
                <div className="mb-3">
                  <Badge bg="info" className="me-2">
                    {result.pageCount} páginas
                  </Badge>
                  <Badge bg="primary" className="me-2">
                    {textFragments.length} fragmentos
                  </Badge>
                  {result.indexedToFirestore && (
                    <Badge bg="success" className="me-2">
                      Indexado en Firestore
                    </Badge>
                  )}
                  {result.embeddingsGenerated && (
                    <Badge bg="info" className="me-2">
                      {result.embeddingsCount || 0} embeddings
                    </Badge>
                  )}
                </div>

                <ListGroup variant="flush" className="border">
                  {textFragments.map((fragment, idx) => (
                    <ListGroup.Item
                      key={fragment.id || idx}
                      className={`${fragment.is_section_header ? "fw-bold" : ""
                        }`}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div className="small text-secondary">
                          Página {fragment.page} | Fragmento {idx + 1}/
                          {textFragments.length}
                        </div>
                        <div>
                          {fragment.section && (
                            <Badge bg="secondary" className="me-1">
                              {fragment.section}
                            </Badge>
                          )}
                          {fragment.context && (
                            <Badge bg="info" className="me-1">
                              {fragment.context}
                            </Badge>
                          )}
                          {fragment.is_partial && (
                            <Badge bg="warning" text="dark" className="me-1">
                              Parte {fragment.part_index}/{fragment.total_parts}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>{fragment.text}</div>
                    </ListGroup.Item>
                  ))}
                  {textFragments.length > 100 && (
                    <ListGroup.Item className="text-center text-primary">
                      <small>
                        Mostrando todos los {textFragments.length} fragmentos.
                        Renderizar muchos fragmentos puede afectar el
                        rendimiento.
                      </small>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Tab>

              <Tab eventKey="sections" title="Secciones">
                {result.metadata && result.metadata.sections && (
                  <ListGroup variant="flush" className="border">
                    {Object.entries(result.metadata.sections).map(
                      ([key, info]) => (
                        <ListGroup.Item key={key}>
                          <h6>{info.title}</h6>
                          <div className="small text-secondary">
                            Tipo: {key} | Página: {info.page || "N/A"}
                          </div>
                        </ListGroup.Item>
                      )
                    )}
                  </ListGroup>
                )}
                {(!result.metadata ||
                  !result.metadata.sections ||
                  Object.keys(result.metadata.sections).length === 0) && (
                    <Alert variant="info">
                      No se detectaron secciones explícitas en este currículum.
                    </Alert>
                  )}
              </Tab>

              <Tab eventKey="contact" title="Información de Contacto">
                {result.metadata && result.metadata.contactInfo && (
                  <ListGroup variant="flush" className="border">
                    {Object.entries(result.metadata.contactInfo).map(
                      ([key, value]) => (
                        <ListGroup.Item key={key}>
                          <div className="fw-bold">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </div>
                          <div>{value}</div>
                        </ListGroup.Item>
                      )
                    )}
                  </ListGroup>
                )}
                {(!result.metadata ||
                  !result.metadata.contactInfo ||
                  Object.keys(result.metadata.contactInfo).length === 0) && (
                    <Alert variant="info">
                      No se detectó información de contacto en este currículum.
                    </Alert>
                  )}
              </Tab>
              <Tab eventKey="rawText" title="Texto Original">
                <div
                  className="border p-3 bg-light"
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                  }}
                >
                  {result.pageTexts &&
                    result.pageTexts.map((pageData, idx) => (
                      <div key={idx} className="mb-4">
                        <h6 className="border-bottom pb-2">
                          Página {pageData.pageNumber}
                        </h6>
                        {pageData.text}
                      </div>
                    ))}
                </div>
              </Tab>
              <Tab
                eventKey="aiAnalysis"
                title="Análisis IA"
                disabled={!result?.analyzedWithAI}
              >
                <Alert variant="info" className="mb-3">
                  <strong>Análisis con IA</strong> - El currículum ha sido
                  analizado con inteligencia artificial para extraer su
                  estructura.
                </Alert>

                <div className="mb-3">
                  {result?.metadata?.aiAnalysis?.anios_experiencia_total ? (
                    <Badge bg="success" className="me-2">
                      {result.metadata.aiAnalysis.anios_experiencia_total} años de experiencia
                    </Badge>
                  ) : (
                    <Badge bg="secondary" className="me-2">
                      Años de experiencia: No calculado
                    </Badge>
                  )}
                </div>

                <Accordion defaultActiveKey="0" className="mb-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Información Personal</Accordion.Header>
                    <Accordion.Body>
                      <ListGroup variant="flush" className="border">
                        {result?.metadata?.aiAnalysis?.informacion_personal && (
                          <>
                            {result.metadata.aiAnalysis.informacion_personal
                              .nombre_completo && (
                                <ListGroup.Item>
                                  <div className="fw-bold">Nombre</div>
                                  <div>
                                    {
                                      result.metadata.aiAnalysis
                                        .informacion_personal.nombre_completo
                                    }
                                  </div>
                                </ListGroup.Item>
                              )}

                            {typeof result.metadata.aiAnalysis
                              .informacion_personal.contacto === "object"
                              ? Object.entries(
                                result.metadata.aiAnalysis
                                  .informacion_personal.contacto
                              ).map(
                                ([key, value]) =>
                                  value && (
                                    <ListGroup.Item key={key}>
                                      <div className="fw-bold">{key}</div>
                                      <div>{value}</div>
                                    </ListGroup.Item>
                                  )
                              )
                              : result.metadata.aiAnalysis.informacion_personal
                                .contacto && (
                                <ListGroup.Item>
                                  <div className="fw-bold">Contacto</div>
                                  <div>
                                    {
                                      result.metadata.aiAnalysis
                                        .informacion_personal.contacto
                                    }
                                  </div>
                                </ListGroup.Item>
                              )}

                            {result.metadata.aiAnalysis.informacion_personal
                              .ubicacion && (
                                <ListGroup.Item>
                                  <div className="fw-bold">Ubicación</div>
                                  <div>
                                    {
                                      result.metadata.aiAnalysis
                                        .informacion_personal.ubicacion
                                    }
                                  </div>
                                </ListGroup.Item>
                              )}
                          </>
                        )}
                      </ListGroup>
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="1">
                    <Accordion.Header>Experiencia Laboral</Accordion.Header>
                    <Accordion.Body>
                      {result?.metadata?.aiAnalysis?.experiencia_laboral ? (
                        result.metadata.aiAnalysis.experiencia_laboral.map(
                          (exp, idx) => (
                            <Card key={idx} className="mb-3">
                              <Card.Header>
                                <strong>{exp.puesto}</strong>{" "}
                                {exp.empresa && `en ${exp.empresa}`}
                              </Card.Header>
                              <Card.Body>
                                <div className="mb-2">
                                  {(exp.fecha_inicio || exp.fecha_fin) && (
                                    <Badge bg="secondary" className="me-2">
                                      {exp.fecha_inicio || ""} -{" "}
                                      {exp.fecha_fin || "Actual"}
                                    </Badge>
                                  )}
                                  {exp.duracion && (
                                    <Badge bg="info">{exp.duracion}</Badge>
                                  )}
                                </div>

                                {exp.ubicacion && (
                                  <p className="mb-2 small text-muted">
                                    {exp.ubicacion}
                                  </p>
                                )}

                                {exp.descripcion_logros && (
                                  <p className="mb-0">
                                    {exp.descripcion_logros}
                                  </p>
                                )}
                              </Card.Body>
                            </Card>
                          )
                        )
                      ) : (
                        <Alert variant="warning">
                          No se encontró información estructurada sobre
                          experiencia laboral
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="2">
                    <Accordion.Header>Educación</Accordion.Header>
                    <Accordion.Body>
                      {result?.metadata?.aiAnalysis?.educacion ? (
                        result.metadata.aiAnalysis.educacion.map((edu, idx) => (
                          <Card key={idx} className="mb-2">
                            <Card.Body>
                              <Card.Title>{edu.titulo}</Card.Title>
                              <Card.Subtitle className="mb-2 text-muted">
                                {edu.institucion}
                              </Card.Subtitle>

                              {edu.campo_estudio && (
                                <Card.Text className="small text-secondary">
                                  Campo de estudio: {edu.campo_estudio}
                                </Card.Text>
                              )}

                              {(edu.fecha_inicio || edu.fecha_fin) && (
                                <div className="mb-2">
                                  <Badge bg="secondary">
                                    {edu.fecha_inicio || ""} -{" "}
                                    {edu.fecha_fin || "Actual"}
                                  </Badge>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        ))
                      ) : (
                        <Alert variant="warning">
                          No se encontró información estructurada sobre
                          educación
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="3">
                    <Accordion.Header>Aptitudes/Habilidades</Accordion.Header>
                    <Accordion.Body>
                      {result?.metadata?.aiAnalysis?.aptitudes ? (
                        <div className="d-flex flex-wrap gap-2">
                          {result.metadata.aiAnalysis.aptitudes.map(
                            (skill, idx) => (
                              <Badge key={idx} bg="primary" className="p-2">
                                {skill}
                              </Badge>
                            )
                          )}
                        </div>
                      ) : (
                        <Alert variant="warning">
                          No se encontraron habilidades estructuradas
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey="4">
                    <Accordion.Header>Certificaciones</Accordion.Header>
                    <Accordion.Body>
                      {result?.metadata?.aiAnalysis?.certificaciones ? (
                        <ListGroup variant="flush" className="border">
                          {result.metadata.aiAnalysis.certificaciones.map(
                            (cert, idx) => (
                              <ListGroup.Item key={idx}>{cert}</ListGroup.Item>
                            )
                          )}
                        </ListGroup>
                      ) : (
                        <Alert variant="warning">
                          No se encontraron certificaciones
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="5">
                    <Accordion.Header>Idiomas</Accordion.Header>
                    <Accordion.Body>
                      {result?.metadata?.aiAnalysis?.idiomas ? (
                        <ListGroup variant="flush" className="border">
                          {result.metadata.aiAnalysis.idiomas.map(
                            (idioma, idx) => (
                              <ListGroup.Item key={idx}>
                                {idioma}
                              </ListGroup.Item>
                            )
                          )}
                        </ListGroup>
                      ) : (
                        <Alert variant="warning">
                          No se encontraron idiomas
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Tab>
              <Tab
                eventKey="jsonData"
                title="JSON Completo"
                disabled={!result?.analyzedWithAI}
              >
                <Alert variant="info" className="mb-3">
                  <strong>Datos en formato JSON</strong> - Respuesta completa del análisis con IA del currículum.
                </Alert>

                <Card>
                  <Card.Body>
                    <div className="mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          if (result?.metadata?.aiAnalysis) {
                            navigator.clipboard.writeText(JSON.stringify(result.metadata.aiAnalysis, null, 2));
                            showNotification("Copiado", "JSON copiado al portapapeles", "success");
                          }
                        }}
                        disabled={!result?.metadata?.aiAnalysis}
                      >
                        Copiar JSON
                      </Button>
                    </div>

                    <div
                      className="border p-3 bg-light"
                      style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        maxHeight: "600px",
                        overflowY: "auto"
                      }}
                    >
                      {result?.metadata?.aiAnalysis ? (
                        JSON.stringify(result.metadata.aiAnalysis, null, 2)
                      ) : (
                        "No hay datos de análisis disponibles"
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default CurriculumExtractor;
